import { IContextualMenuProps, IPivotItemProps, useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandButton, IPFontIcon, IPPivot, IPScrollablePane, IPSpinner } from 'baseComponents';
import { IPContextualMenu, IPContextualMenuItemType } from 'baseComponents/ContextualMenu/IPContextualMenu';
import { IPPivotItem } from 'baseComponents/Pivot/IPPivotItem';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import LinkConfirmationModal from 'components/Links/Preview/LinkConfirmationModal';
import { AuditActionPickerGroupCodes } from 'components/Pickers/AuditActionPicker';
import {
  deleteIcon,
  downArrowIcon,
  editIcon,
  getFontIconColorPrimary,
  globalStackItemStylesScroll,
  globalStackTokensGapSmall,
  leftArrowIcon,
  newIcon,
  rightArrowIcon,
  shareIcon,
} from 'globalStyles';
import AdminTenant from 'models/adminTenant';
import Dashboard, { DashboardSharedData, DashboardTypes, Dashboard_Translation } from 'models/dashboard';
import DashboardScene from 'models/dashboardScene';
import { EntityTypes } from 'models/entity';
import Language from 'models/language';
import Norm, { Standards } from 'models/norm';
import { ResourceListType } from 'models/resourceList';
import Widget, { WidgetSizes, WidgetTypes, Widget_Translation } from 'models/widget';
import { MouseEvent as ReactMouseEvent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { apiGetAuditActions } from 'services/Api/auditService';
import {
  apiAddDashboard,
  apiAddWidgetToDashboard,
  apiDeleteDashboard,
  apiGetDashboardsForType,
  apiUpdateDashboard,
} from 'services/Api/dashboardService';
import { apiGetListsForTenants } from 'services/Api/listService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { fromStringToNumberArray, numberArrayToString } from 'utils/array';
import { LocalStorageKeys, getLocalStorageData, setLocalStorageData } from 'utils/localstorage';
import { overflow } from 'utils/string';
import AddDashboardPanel from './AddDashboardPanel';
import DashboardInstance from './DashboardInstance';
import EditDashboardPanel from './EditDashboardPanel';
import ShareDashboardModal from './ShareDashboardModal';
import { WidgetCustomNormCoverageConfig } from './Widgets/WidgetCustomNormCoverage';
import { WidgetMyUpcomingTasksConfig } from './Widgets/WidgetMyUpcomingTasks';
import { WidgetNormCoverageConfig } from './Widgets/WidgetNormCoverage';
import { WidgetTenantControlChangesConfig } from './Widgets/WidgetTenantControlChanges';
import { WidgetTenantControlStatusConfig } from './Widgets/WidgetTenantControlStatus';
import { WidgetTenantListApprovalStatusConfig } from './Widgets/WidgetTenantListApprovalStatus';
import { WidgetTenantRiskChangesConfig } from './Widgets/WidgetTenantRiskChanges';
import { WidgetTenantRiskOwnerConfig } from './Widgets/WidgetTenantRiskOwner';
import { WidgetTenantRiskStatusConfig } from './Widgets/WidgetTenantRiskStatus';
import { WidgetTenantTaskStatusConfig } from './Widgets/WidgetTenantTaskStatus';
import { WidgetTenantUserStatsConfig } from './Widgets/WidgetTenantUserStats';
import { getISONormsForTenants } from './Widgets/tenantWidgetData';

interface IDashboardCollectionProps {
  dashboardScene?: DashboardScene;
  dashboardType: DashboardTypes;
  partnerCustomers?: AdminTenant[];
  onRefreshSharedData?: () => Promise<void>;
}

const DashboardCollection = (props: IDashboardCollectionProps) => {
  //load some common translation files upfront
  const { t } = useTranslation([
    'translation',
    'dashboard',
    'widgets',
    'task',
    'tasks',
    'risk',
    'control',
    'theme',
    'objective',
    'process',
    'kpi',
    'asset',
    'library',
    'exceptions',
    'adminAuth',
    'partnerAdmin',
  ]);
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const location = useLocation();
  const [selectedPivot, setSelectedPivot] = useState<string | undefined>(undefined);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addDashboard, setAddDashboard] = useState<Dashboard | undefined>(undefined);
  const [editDashboard, setEditDashboard] = useState<Dashboard | undefined>(undefined);
  const [deleteDashboard, setDeleteDashboard] = useState<Dashboard | undefined>(undefined);
  const [shareDashboard, setShareDashboard] = useState<Dashboard | undefined>(undefined);
  const [currentType, setCurrentType] = useState<number | undefined>(undefined);
  const [sharedData, setSharedData] = useState<DashboardSharedData | undefined>(undefined);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [showConfirmationModalLinkId, setShowConfirmationModalLinkId] = useState<number | undefined>(undefined);
  const loadingDashboardTypeRef = useRef<DashboardTypes | undefined>(undefined);
  const [dashboardMenu, setDashboardMenu] = useState<{ pivotKey: string; target: HTMLElement } | undefined>(undefined);
  const maxDashboardsPerType = 8;
  const partnerDefaultDashboardCreationKey = 'isoplanner.partnerDefaultDashboardCreating';
  //A guard against two TABS seeding the default partner dashboard at once - unlike the per-customer dashboard,
  //POST dashboards has no idempotent server side, so both would succeed and the partner would end up with two.
  //It therefore lives in localStorage, which is shared across tabs of the same origin; sessionStorage is per tab
  //and would only have repeated what loadingDashboardTypeRef already prevents. It is timestamped because the
  //finally that clears it does not run if the tab is closed or reloaded mid-creation, and a stale key used to
  //strand the partner on an empty dashboard for the rest of the browser session.
  const partnerDefaultDashboardCreationTimeoutMs = 60000;

  const isPartnerDashboardCreationInProgress = (): boolean => {
    const startedAt = window.localStorage.getItem(partnerDefaultDashboardCreationKey);
    if (!startedAt) return false;

    const startedAtMs = Number(startedAt);
    if (!Number.isFinite(startedAtMs) || Date.now() - startedAtMs > partnerDefaultDashboardCreationTimeoutMs) {
      window.localStorage.removeItem(partnerDefaultDashboardCreationKey);

      return false;
    }

    return true;
  };

  useEffect(() => {
    const controller = new AbortController();

    const refreshSharedData = () => {
      const newSharedData = new DashboardSharedData();
      newSharedData.activities = props.dashboardScene?.activities;
      newSharedData.myTasks = props.dashboardScene?.tasks;
      newSharedData.userOrgUnits = props.dashboardScene?.userOrgUnits;
      newSharedData.partnerCustomers = props.partnerCustomers;
      setSharedData(newSharedData);
    };

    const loadData = async () => {
      try {
        await appContext.globalDataCache.norms.getItems();
        refreshSharedData();

        if (props.dashboardType !== currentType) {
          if (loadingDashboardTypeRef.current === props.dashboardType) {
            return;
          }

          loadingDashboardTypeRef.current = props.dashboardType;
          setIsLoading(true);
          setCurrentType(props.dashboardType);
          //full load
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          if (controller.signal.aborted) return;

          let newDashboards = await apiGetDashboardsForType(
            props.dashboardType,
            accessToken,
            appContext.globalDataCache,
            controller.signal,
          );
          if (controller.signal.aborted) return;

          //The first dashboard is always the system dashboard and cannot be changed
          //This dashboard also has the default widgets. The user can add widgets so these are merged into the default dashboard from API
          if (props.dashboardType === DashboardTypes.user) {
            if (newDashboards.length === 0) {
              //add the system dashboard with default widgets
              const newMyDashboard = getMyDefaultDashboard(props.dashboardScene);
              const createdDashboard = await apiAddDashboard(newMyDashboard, accessToken, appContext.globalDataCache);
              createdDashboard.widgets = newMyDashboard.widgets;
              for (let idx = 0; idx < createdDashboard.widgets.length; idx++) {
                let widget = createdDashboard.widgets[idx];
                widget.dashboard = createdDashboard;
                widget.dashboardId = createdDashboard.dashboardId;
                widget = await apiAddWidgetToDashboard(widget, accessToken, appContext.globalDataCache);
              }
              createdDashboard.isSystem = true;
              createdDashboard.widgets = getMySystemWidgets(newMyDashboard);
              newDashboards.push(createdDashboard);
            } else {
              //add the system widgets to the first dashboard
              //sort by id to make sure the first created dashboard is always at the start which is the system dashboard
              newDashboards.sort((a, b) => a.dashboardId - b.dashboardId);
              const myDashboard = newDashboards[0];
              myDashboard.isSystem = true;
              myDashboard.widgets.push(...getMySystemWidgets(myDashboard));
              myDashboard.name = t('dashboard:DefaultName');
            }
          } else if (props.dashboardType === DashboardTypes.org) {
            if (newDashboards.length === 0 && hasUserFeatureGenericManager(appContext)) {
              //create a default organization dashboard when no organization dashboard exists
              const norms = appContext.globalDataCache.norms.items.sort((a, b) => a.name.localeCompare(b.name));
              const newOrgDashboard = getOrgDefaultDashboard(norms, props.dashboardScene);
              //add the default dashboard and the widgets
              const createdDashboard = await apiAddDashboard(newOrgDashboard, accessToken, appContext.globalDataCache);
              for (let idx = 0; idx < newOrgDashboard.widgets.length; idx++) {
                const widget = newOrgDashboard.widgets[idx];
                widget.dashboard = createdDashboard;
                widget.dashboardId = createdDashboard.dashboardId;
                await apiAddWidgetToDashboard(widget, accessToken, appContext.globalDataCache);
                //don't need to add the widget to the dashboard. widgets will be loaded in the dashboard instance component
              }
              newDashboards.push(createdDashboard);
            }
          } else if (props.dashboardType === DashboardTypes.partner) {
            if (newDashboards.length === 0 && hasUserFeatureGenericManager(appContext)) {
              if (!isPartnerDashboardCreationInProgress()) {
                try {
                  window.localStorage.setItem(partnerDefaultDashboardCreationKey, Date.now().toString());
                  const newPartnerDashboard = await getPartnerDefaultDashboard(accessToken);

                  const createdDashboard = await apiAddDashboard(
                    newPartnerDashboard,
                    accessToken,
                    appContext.globalDataCache,
                  );
                  for (let idx = 0; idx < newPartnerDashboard.widgets.length; idx++) {
                    const widget = newPartnerDashboard.widgets[idx];
                    widget.dashboard = createdDashboard;
                    widget.dashboardId = createdDashboard.dashboardId;
                    await apiAddWidgetToDashboard(widget, accessToken, appContext.globalDataCache);
                  }
                  newDashboards.push(createdDashboard);
                } finally {
                  window.localStorage.removeItem(partnerDefaultDashboardCreationKey);
                }
              } else {
                newDashboards = await apiGetDashboardsForType(
                  props.dashboardType,
                  accessToken,
                  appContext.globalDataCache,
                );
              }
            }
          }

          //set the sort order
          if (props.dashboardType === DashboardTypes.org) {
            newDashboards.sort((a, b) => a.sortOrder - b.sortOrder);
          } else {
            newDashboards = setSortOrderFromStorage(newDashboards, props.dashboardType);
          }

          setDashboards(newDashboards);
          processHashesFromUrl(newDashboards);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        appContext.setError(err);
      } finally {
        loadingDashboardTypeRef.current = undefined;
        if (controller.signal.aborted) return;
        setIsLoading(false);
      }
    };

    if (props.dashboardScene || props.dashboardType === DashboardTypes.partner) {
      loadData();
    }

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dashboardType, props.dashboardScene, props.partnerCustomers]);

  useEffect(() => {
    processHashesFromUrl(dashboards);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const processHashesFromUrl = (dashboards: Dashboard[]) => {
    const urlHash = window.location.hash;
    if (urlHash) {
      if (urlHash.startsWith('#readConfirmations')) {
        const linkId = Number.parseInt(urlHash.split('@')[1] || '0');
        if (linkId) {
          setShowConfirmationModalLinkId(linkId);
        }
        setShowConfirmationModal(true);
      } else if (dashboards && dashboards.length > 0) {
        const dashboard = dashboards.find((d) => '#' + d.dashboardId.toString() === urlHash);
        if (dashboard) {
          const key = getKey(dashboard);
          if (key !== selectedPivot) {
            setSelectedPivot(getKey(dashboard));
          }
        } else {
          // No matching dashboard, clear the hash
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }
    }
    // Only select the first dashboard if nothing is selected and no hash is present
    if (!urlHash && dashboards && dashboards.length > 0 && !selectedPivot) {
      setSelectedPivot(getKey(dashboards[0]));
    }
  };

  const setSortOrderFromStorage = (dashboards: Dashboard[], type: DashboardTypes): Dashboard[] => {
    let key: LocalStorageKeys;
    switch (type) {
      case DashboardTypes.user:
        key = LocalStorageKeys.DashboardSortOrderForMe;
        break;
      case DashboardTypes.team:
        key = LocalStorageKeys.DashboardSortOrderForMyTeams;
        break;
      default:
        dashboards.sort((a, b) => a.name.localeCompare(b.name));

        return dashboards;
    }

    const orderStr = getLocalStorageData(appContext, key);
    if (orderStr) {
      const order = fromStringToNumberArray(orderStr);
      let newDashboardOrder: Dashboard[] = [];
      for (let idx = 0; idx < order.length; idx++) {
        const dashboard = dashboards.find((d) => d.dashboardId === order[idx]);
        if (dashboard) {
          newDashboardOrder.push(dashboard);
        }
      }
      const dashboardsNotInStorage = dashboards.filter((d) => !order.includes(d.dashboardId));
      if (dashboardsNotInStorage.length > 0) {
        dashboardsNotInStorage.sort((a, b) => a.name.localeCompare(b.name));
        newDashboardOrder.push(...dashboardsNotInStorage);
      }
      newDashboardOrder = moveSystemDashboardToFront(newDashboardOrder);
      saveSortOrderToStorage(newDashboardOrder, type);

      return newDashboardOrder;
    } else {
      dashboards.sort((a, b) => a.name.localeCompare(b.name));
      dashboards = moveSystemDashboardToFront(dashboards);
      saveSortOrderToStorage(dashboards, type);

      return dashboards;
    }
  };

  const moveSystemDashboardToFront = (dashboards: Dashboard[]): Dashboard[] => {
    const idx = dashboards.findIndex((d) => d.isSystem);
    if (idx > 0) {
      const systemDashboard = dashboards[idx];
      dashboards.splice(idx, 1);

      return [systemDashboard, ...dashboards];
    }

    return dashboards;
  };

  const saveSortOrderToApi = async (dashboard: Dashboard) => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiUpdateDashboard(dashboard, accessToken, appContext.globalDataCache);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const saveSortOrderToStorage = (dashboards: Dashboard[], type: DashboardTypes) => {
    let storageKey: LocalStorageKeys;
    switch (type) {
      case DashboardTypes.user:
        storageKey = LocalStorageKeys.DashboardSortOrderForMe;
        break;
      case DashboardTypes.team:
        storageKey = LocalStorageKeys.DashboardSortOrderForMyTeams;
        break;
      default:
        return;
    }

    const orderStr = numberArrayToString(dashboards.map((d) => d.dashboardId));
    setLocalStorageData(appContext, storageKey, orderStr);
  };

  const onDeleteDashboard = async () => {
    try {
      if (isLoading || !deleteDashboard) return;
      setIsLoading(true);
      setDeleteDashboard(undefined);
      appContext.showContentLoader();

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiDeleteDashboard(deleteDashboard, accessToken);

      const newDashboards = dashboards.filter((d) => d.dashboardId !== deleteDashboard.dashboardId);

      setDashboards(newDashboards);
      saveSortOrder(deleteDashboard, newDashboards, props.dashboardType, false);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
      appContext.hideContentLoader();
    }
  };

  const onCreateNewDashboard = async (newDashboard: Dashboard) => {
    try {
      if (isLoading || !addDashboard) return;
      setIsLoading(true);
      setAddDashboard(undefined);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      newDashboard = await apiAddDashboard(newDashboard, accessToken, appContext.globalDataCache);

      const idx = dashboards.findIndex((d) => d.dashboardId === addDashboard.dashboardId);
      const before = dashboards.slice(0, idx + 1);
      const after = dashboards.slice(idx + 1);
      const newDashboards = [...before, newDashboard, ...after];

      setDashboards(newDashboards);
      saveSortOrder(newDashboard, newDashboards, props.dashboardType, false);
      setSelectedPivot(getKey(newDashboard));
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onEditDashboard = async (updatedDashboard: Dashboard) => {
    try {
      if (isLoading) return;
      setIsLoading(true);
      setEditDashboard(undefined);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiUpdateDashboard(updatedDashboard, accessToken, appContext.globalDataCache);

      if (shareDashboard) {
        appContext.showNotification(t('dashboard:Share.Success'));
        setShareDashboard(undefined);
      }

      setDashboards(dashboards.map((d) => (d.dashboardId === updatedDashboard.dashboardId ? updatedDashboard : d)));
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Default dashboards
  //
  const getMyDefaultDashboard = (dashboardScene: DashboardScene | undefined): Dashboard => {
    const newDashboard = new Dashboard(DashboardTypes.user);
    newDashboard.dashboardId = -1; //set to -1 to indicate system dashboard
    newDashboard.name = t('dashboard:DefaultName');
    newDashboard.trans = [new Dashboard_Translation()];
    newDashboard.trans[0].lang = new Language();
    newDashboard.trans[0].lang.code = appContext.user.language.code;
    newDashboard.transIdx = 0;

    const myUpcomingTasksWidget = new Widget();
    myUpcomingTasksWidget.dashboard = newDashboard;
    myUpcomingTasksWidget.dashboardId = newDashboard.dashboardId;
    myUpcomingTasksWidget.sortOrder = 0;
    myUpcomingTasksWidget.widgetType = WidgetTypes.MyUpcomingTasks;
    const config = new WidgetMyUpcomingTasksConfig();
    config.period = 14;
    myUpcomingTasksWidget.widgetConfig = JSON.stringify(config);
    newDashboard.widgets.push(myUpcomingTasksWidget);

    const myOverdueTasksWidget = new Widget();
    myOverdueTasksWidget.dashboard = newDashboard;
    myOverdueTasksWidget.dashboardId = newDashboard.dashboardId;
    myOverdueTasksWidget.sortOrder = 1;
    myOverdueTasksWidget.widgetType = WidgetTypes.MyOverdueTasks;
    newDashboard.widgets.push(myOverdueTasksWidget);

    return newDashboard;
  };

  const getMySystemWidgets = (dashboard: Dashboard): Widget[] => {
    const defWidgets: Widget[] = [];

    const myRecentItemsWidget = new Widget();
    myRecentItemsWidget.dashboard = dashboard;
    myRecentItemsWidget.dashboardId = dashboard.dashboardId;
    myRecentItemsWidget.widgetId = -2;
    myRecentItemsWidget.sortOrder = -2;
    myRecentItemsWidget.name = t('dashboard:RecentItems.MyRecentItems');
    myRecentItemsWidget.widgetType = WidgetTypes.MyRecentItems;
    defWidgets.push(myRecentItemsWidget);

    const myActionsWidget = new Widget();
    myActionsWidget.dashboard = dashboard;
    myActionsWidget.dashboardId = dashboard.dashboardId;
    myActionsWidget.widgetId = -1;
    myRecentItemsWidget.sortOrder = -1;
    myActionsWidget.name = t('dashboard:Action.ActionRequired');
    myActionsWidget.widgetType = WidgetTypes.MyActions;
    defWidgets.push(myActionsWidget);

    return defWidgets;
  };

  const getOrgDefaultDashboard = (norms: Norm[], dashboardScene: DashboardScene | undefined): Dashboard => {
    const newDashboard = new Dashboard(DashboardTypes.org);
    newDashboard.name = appContext.user.tenant.name;
    newDashboard.trans = [new Dashboard_Translation()];
    newDashboard.trans[0].lang = new Language();
    newDashboard.trans[0].lang.code = appContext.user.language.code;
    newDashboard.transIdx = 0;

    const riskStatusWidget = new Widget();
    riskStatusWidget.dashboard = newDashboard;
    riskStatusWidget.dashboardId = newDashboard.dashboardId;
    riskStatusWidget.sortOrder = 0;
    riskStatusWidget.widgetType = WidgetTypes.RiskStatus;
    newDashboard.widgets.push(riskStatusWidget);

    for (let idx = 0; idx < norms.length; idx++) {
      const norm = norms[idx];
      const normWidget = new Widget();
      normWidget.dashboard = newDashboard;
      normWidget.dashboardId = newDashboard.dashboardId;
      normWidget.sortOrder = idx + 1;

      if (norm.isoNormId) {
        normWidget.widgetType = WidgetTypes.NormCoverage;
        const config = new WidgetNormCoverageConfig();
        config.isoNormId = norm.isoNormId;
        normWidget.widgetConfig = JSON.stringify(config);
      } else {
        normWidget.widgetType = WidgetTypes.CustomNormCoverage;
        const config = new WidgetCustomNormCoverageConfig();
        config.customNormId = norm.normId;
        normWidget.widgetConfig = JSON.stringify(config);
      }

      newDashboard.widgets.push(normWidget);
    }

    const openTasksWidget = new Widget();
    openTasksWidget.dashboard = newDashboard;
    openTasksWidget.dashboardId = newDashboard.dashboardId;
    openTasksWidget.sortOrder = newDashboard.widgets.length;
    openTasksWidget.widgetType = WidgetTypes.OpenTasksPerUser;
    newDashboard.widgets.push(openTasksWidget);

    return newDashboard;
  };

  const getPartnerDefaultDashboard = async (accessToken: string): Promise<Dashboard> => {
    const customerIds = props.partnerCustomers?.map((customer) => customer.tenantId) ?? [];
    const newDashboard = new Dashboard(DashboardTypes.partner);
    newDashboard.name = t('partnerAdmin:DashboardTitle');
    newDashboard.trans = [new Dashboard_Translation()];
    newDashboard.trans[0].lang = new Language();
    newDashboard.trans[0].lang.code = appContext.user.language.code;
    newDashboard.transIdx = 0;

    addPartnerTenantWidget(
      newDashboard,
      WidgetTypes.TenantRiskStatusPartner,
      getPartnerTenantConfig(WidgetTenantRiskStatusConfig, customerIds),
    );

    const riskChangesConfig = getPartnerTenantConfig(WidgetTenantRiskChangesConfig, customerIds);
    riskChangesConfig.actions = await getPartnerAuditActionIds(EntityTypes.Risk, accessToken);
    addPartnerTenantWidget(newDashboard, WidgetTypes.TenantRiskChangesPartner, riskChangesConfig);

    addPartnerTenantWidget(
      newDashboard,
      WidgetTypes.TenantRiskOwnerPartner,
      getPartnerTenantConfig(WidgetTenantRiskOwnerConfig, customerIds),
    );

    const defaultISONormId = getDefaultPartnerISONormId();
    if (defaultISONormId) {
      const controlStatusConfig = getPartnerTenantConfig(WidgetTenantControlStatusConfig, customerIds);
      controlStatusConfig.isoNormId = defaultISONormId;
      addPartnerTenantWidget(newDashboard, WidgetTypes.TenantControlStatusPartner, controlStatusConfig);
    }

    const controlChangesConfig = getPartnerTenantConfig(WidgetTenantControlChangesConfig, customerIds);
    controlChangesConfig.actions = await getPartnerAuditActionIds(EntityTypes.Control, accessToken);
    addPartnerTenantWidget(newDashboard, WidgetTypes.TenantControlChangesPartner, controlChangesConfig);

    if (customerIds.length > 0) {
      const listApprovalConfig = await getPartnerListApprovalConfig(customerIds, accessToken);
      if (Object.keys(listApprovalConfig.lists).length > 0) {
        addPartnerTenantWidget(newDashboard, WidgetTypes.TenantListApprovalStatusPartner, listApprovalConfig);
      }
    }

    addPartnerTenantWidget(
      newDashboard,
      WidgetTypes.TenantTaskStatusPartner,
      getPartnerTenantConfig(WidgetTenantTaskStatusConfig, customerIds),
    );

    const taskStatusOverDeadlineConfig = getPartnerTenantConfig(WidgetTenantTaskStatusConfig, customerIds);
    taskStatusOverDeadlineConfig.overDeadline = true;
    addPartnerTenantWidget(
      newDashboard,
      WidgetTypes.TenantTaskStatusPartner,
      taskStatusOverDeadlineConfig,
      t('widgets:TenantTaskStatusPartner.OverDeadlineName'),
    );

    addPartnerTenantWidget(
      newDashboard,
      WidgetTypes.TenantUserStatsPartner,
      getPartnerTenantConfig(WidgetTenantUserStatsConfig, customerIds),
    );

    return newDashboard;
  };

  const getPartnerTenantConfig = <TConfig extends { tenants: string[] }>(
    configType: new () => TConfig,
    tenantIds: string[],
  ): TConfig => {
    const config = new configType();
    config.tenants = [...tenantIds];

    return config;
  };

  const getPartnerListApprovalConfig = async (
    tenantIds: string[],
    accessToken: string,
  ): Promise<WidgetTenantListApprovalStatusConfig> => {
    const config = new WidgetTenantListApprovalStatusConfig();
    if (tenantIds.length === 0) return config;

    const lists = await apiGetListsForTenants(tenantIds, accessToken);
    for (const list of lists) {
      if (
        list.tenantId &&
        (list.listType === ResourceListType.DocumentLibrary || list.listType === ResourceListType.SitePageLibrary)
      ) {
        config.lists[list.tenantId] = [...(config.lists[list.tenantId] ?? []), list.listId];
      }
    }

    return config;
  };

  const getPartnerAuditActionIds = async (entityType: EntityTypes.Risk | EntityTypes.Control, accessToken: string) => {
    const groupCode = AuditActionPickerGroupCodes[entityType];
    const actions = await apiGetAuditActions(accessToken);

    return actions.filter((action) => action.code.startsWith(groupCode)).map((action) => action.auditTrailActionId);
  };

  //The standard to seed the Control status widget with, taken from what the CUSTOMERS run. Reading
  //globalDataCache here would read the partner's own installed standards, so a consultancy that runs none itself
  //never got the widget on its default dashboard at all - which hid the problem rather than showing it.
  const getDefaultPartnerISONormId = (): number | undefined => {
    const isoNorms = getISONormsForTenants(props.partnerCustomers ?? [], []).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const iso27001 = isoNorms.find((norm) => norm.isoNormId === Standards.ISO27001_2022);

    if (iso27001) {
      return iso27001.isoNormId;
    }

    return isoNorms[0]?.isoNormId;
  };

  const addPartnerTenantWidget = (dashboard: Dashboard, widgetType: WidgetTypes, config: object, name?: string) => {
    const widget = new Widget();
    widget.dashboard = dashboard;
    widget.dashboardId = dashboard.dashboardId;
    widget.sortOrder = dashboard.widgets.length;
    widget.widgetType = widgetType;
    widget.widgetSize = WidgetSizes.Medium;
    widget.widgetConfig = JSON.stringify(config);
    if (name) {
      widget.name = name;
      widget.trans = [new Widget_Translation()];
      widget.trans[0].lang = new Language();
      widget.trans[0].lang.code = appContext.user.language.code;
      widget.trans[0].name = name;
      widget.transIdx = 0;
    }
    dashboard.widgets.push(widget);
  };

  //
  // Menu items
  //
  const handleLinkClick = (item: IPPivotItem | undefined) => {
    if (item && item.props.itemKey) {
      setSelectedPivot(item.props.itemKey);
      window.location.hash = getDashboardFromPivotKey(item.props.itemKey)?.dashboardId.toString();
    } else {
      setSelectedPivot(undefined);
    }
  };

  const getKey = (dashBoard: Dashboard | undefined): string | undefined => {
    if (!dashBoard) return undefined;

    return 'dashboard' + dashBoard.dashboardId.toString();
  };

  const getId = (pivotKey: string): number => {
    const id = pivotKey.slice('dashboard'.length - pivotKey.length);

    return Number.parseInt(id);
  };

  const getDashboardFromPivotKey = (pivotKey: string): Dashboard => {
    const id = getId(pivotKey);

    return dashboards.find((d) => d.dashboardId === id) || new Dashboard(props.dashboardType);
  };

  const getDashboardIndexFromPivotKey = (pivotKey: string): number => {
    const id = getId(pivotKey);

    return dashboards.findIndex((d) => d.dashboardId === id);
  };

  const saveSortOrder = (dashboard: Dashboard, dashboards: Dashboard[], type: DashboardTypes, isMove: boolean) => {
    switch (type) {
      case DashboardTypes.user:
      case DashboardTypes.team:
        saveSortOrderToStorage(dashboards, type);
        break;
      case DashboardTypes.org:
        //inserts/deletes are handled by the Api at the time of insert/delete
        if (isMove) saveSortOrderToApi(dashboard);
        break;
      default:
        return;
    }
  };

  const moveLeft = (pivotKey: string) => {
    const id = getId(pivotKey);
    const newDashBoards = [...dashboards];
    const idx = newDashBoards.findIndex((d) => d.dashboardId === id);
    if (idx > 0) {
      const dashboard = newDashBoards[idx];
      dashboard.sortOrder--;
      [newDashBoards[idx - 1], newDashBoards[idx]] = [newDashBoards[idx], newDashBoards[idx - 1]];
      setDashboards(newDashBoards);
      saveSortOrder(dashboard, newDashBoards, props.dashboardType, true);
    }
  };

  const moveRight = (pivotKey: string) => {
    const id = getId(pivotKey);
    const newDashBoards = [...dashboards];
    const idx = dashboards.findIndex((d) => d.dashboardId === id);
    if (idx >= 0 && idx < newDashBoards.length - 1) {
      const dashboard = newDashBoards[idx];
      dashboard.sortOrder++;
      [newDashBoards[idx + 1], newDashBoards[idx]] = [newDashBoards[idx], newDashBoards[idx + 1]];
      setDashboards(newDashBoards);
      saveSortOrder(dashboard, newDashBoards, props.dashboardType, true);
    }
  };

  const canAddDashboard = (pivotKey: string): boolean => {
    const dashboard = getDashboardFromPivotKey(pivotKey);

    //need manager/admin role to add dashboards other then user-type
    if (dashboard.dashboardType !== DashboardTypes.user && !hasUserFeatureGenericManager(appContext)) {
      return false;
    }

    //max count reached
    if (dashboards.length < maxDashboardsPerType) {
      return true;
    }

    return false;
  };

  const canMoveLeft = (pivotKey: string): boolean => {
    const idx = getDashboardIndexFromPivotKey(pivotKey);
    const dashboard = getDashboardFromPivotKey(pivotKey);

    if (dashboard.dashboardType === DashboardTypes.user) {
      return idx > 1; //cannot move before user system dashboard
    } else {
      return idx > 0;
    }
  };

  const canMoveRight = (pivotKey: string): boolean => {
    const idx = getDashboardIndexFromPivotKey(pivotKey);
    const dashboard = getDashboardFromPivotKey(pivotKey);

    if (dashboard.dashboardType === DashboardTypes.user) {
      return idx > 0 && idx < dashboards.length - 1; //cannot move user system dashboard
    } else {
      return idx < dashboards.length - 1;
    }
  };

  const canShare = (pivotKey: string): boolean => {
    const dashboard = getDashboardFromPivotKey(pivotKey);

    return dashboard.dashboardType !== DashboardTypes.org && !isSystem(pivotKey);
  };

  const isSystem = (pivotKey: string): boolean => {
    const dashboard = getDashboardFromPivotKey(pivotKey);

    return dashboard.isSystem;
  };

  const isSharedWithMe = (pivotKey: string): boolean => {
    return getDashboardFromPivotKey(pivotKey).isSharedWithUser(appContext.user.id);
  };

  const isReadOnly = (dashboard: Dashboard): boolean => {
    // 1. dashboard is shared
    // 2. dashboard type is team or org and and has not roles
    return (
      dashboard.isSharedWithUser(appContext.user.id) ||
      (dashboard.dashboardType !== DashboardTypes.user && !hasUserFeatureGenericManager(appContext))
    );
  };

  const getMenuItems = (pivotKey: string): IContextualMenuProps => {
    return {
      items: [
        {
          key: 'add',
          disabled: !canAddDashboard(pivotKey),
          data: pivotKey,
          iconProps: newIcon,
          text: t('dashboard:Menu.New'),
          onClick: () => setAddDashboard(getDashboardFromPivotKey(pivotKey)),
        },
        { key: 'd1', itemType: IPContextualMenuItemType.Divider },
        {
          key: 'moveleft',
          disabled: !canMoveLeft(pivotKey),
          data: pivotKey,
          iconProps: leftArrowIcon,
          text: t('dashboard:Menu.MoveLeft'),
          onClick: () => moveLeft(pivotKey),
        },
        {
          key: 'moveright',
          disabled: !canMoveRight(pivotKey),
          data: pivotKey,
          iconProps: rightArrowIcon,
          text: t('dashboard:Menu.MoveRight'),
          onClick: () => moveRight(pivotKey),
        },
        { key: 'd3', itemType: IPContextualMenuItemType.Divider },
        {
          key: 'edit',
          disabled: isSystem(pivotKey) || isSharedWithMe(pivotKey),
          data: pivotKey,
          iconProps: editIcon,
          text: t('dashboard:Menu.Edit'),
          onClick: () => setEditDashboard(getDashboardFromPivotKey(pivotKey)),
        },
        {
          key: 'delete',
          disabled: isSystem(pivotKey) || isSharedWithMe(pivotKey),
          data: pivotKey,
          iconProps: deleteIcon,
          text: t('dashboard:Menu.Delete'),
          onClick: () => setDeleteDashboard(getDashboardFromPivotKey(pivotKey)),
        },
        ...(props.dashboardType === DashboardTypes.partner
          ? []
          : [
              { key: 'd2', data: pivotKey, itemType: IPContextualMenuItemType.Divider },
              {
                key: 'share',
                data: pivotKey,
                disabled: !canShare(pivotKey),
                iconProps: shareIcon,
                text: t('dashboard:Menu.Share'),
                onClick: () => setShareDashboard(getDashboardFromPivotKey(pivotKey)),
              },
            ]),
      ],
    };
  };

  const preventPivotClick = (event: ReactMouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const openDashboardMenu = (event: ReactMouseEvent<HTMLElement>, pivotKey: string) => {
    preventPivotClick(event);
    setDashboardMenu({ pivotKey: pivotKey, target: event.currentTarget });
  };

  const openShareDashboard = (event: ReactMouseEvent<HTMLElement>, pivotKey: string) => {
    preventPivotClick(event);
    setShareDashboard(getDashboardFromPivotKey(pivotKey));
  };

  //
  // Render
  //

  const renderItem = (
    itemProps?: IPivotItemProps,
    defaultRender?: (itemProps?: IPivotItemProps) => JSX.Element | null,
  ): JSX.Element | null => {
    if (!itemProps || !itemProps.itemKey || !defaultRender) return null;

    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        {defaultRender(itemProps)}
        {(props.dashboardType === DashboardTypes.user || hasUserFeatureGenericManager(appContext)) && (
          <IPStack.Item>
            <span
              onClick={(event) => openDashboardMenu(event, itemProps.itemKey ?? '')}
              onMouseDown={preventPivotClick}
              style={{ alignItems: 'center', cursor: 'pointer', display: 'inline-flex', height: 32, width: 24 }}
            >
              <IPFontIcon
                {...downArrowIcon}
                style={{ color: getFontIconColorPrimary(appContext.useDarkMode, theme), paddingLeft: 4, paddingTop: 4 }}
              />
            </span>
          </IPStack.Item>
        )}
        {getDashboardFromPivotKey(itemProps.itemKey).isSharedWithUser(appContext.user.id) && (
          <IPStack.Item>
            <span
              onClick={(event) => openShareDashboard(event, itemProps.itemKey ?? '')}
              onMouseDown={preventPivotClick}
              style={{ alignItems: 'center', cursor: 'pointer', display: 'inline-flex', height: 32, width: 24 }}
            >
              <IPFontIcon
                iconName="ReminderPerson"
                style={{ color: getFontIconColorPrimary(appContext.useDarkMode, theme), paddingTop: 4 }}
              />
            </span>
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!addDashboard && dashboards.length === 0) {
    return (
      <IPCommandButton
        disabled={!hasUserFeatureGenericManager(appContext)}
        text={t('dashboard:Menu.New')}
        iconProps={newIcon}
        onClick={() => setAddDashboard(new Dashboard(props.dashboardType))}
      />
    );
  }

  return (
    <IPStack verticalFill>
      <IPStack.Item grow styles={globalStackItemStylesScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPPivot overflowBehavior="menu" onLinkClick={handleLinkClick} selectedKey={selectedPivot}>
            {dashboards.map((dashboard) => {
              return (
                <IPPivotItem
                  key={getKey(dashboard)}
                  headerText={overflow(dashboard.name, 32)}
                  itemKey={getKey(dashboard)}
                  onRenderItemLink={renderItem}
                >
                  <DashboardInstance
                    dashboard={dashboard}
                    sharedData={sharedData}
                    readonly={isReadOnly(dashboard)}
                    onRefreshSharedData={props.onRefreshSharedData}
                  />
                </IPPivotItem>
              );
            })}
          </IPPivot>
        </IPScrollablePane>
      </IPStack.Item>
      {dashboardMenu && (
        <IPContextualMenu
          {...getMenuItems(dashboardMenu.pivotKey)}
          target={dashboardMenu.target}
          onDismiss={() => setDashboardMenu(undefined)}
        />
      )}
      <DialogConfirmDelete
        onNo={() => {
          setDeleteDashboard(undefined);
        }}
        onYes={() => {
          onDeleteDashboard();
        }}
        title={t('dashboard:Dialogs.Delete.Title')}
        subText={t('dashboard:Dialogs.Delete.SubTitle')}
        hidden={deleteDashboard === undefined}
        confirmText={t('dashboard:Dialogs.Delete.ConfirmText')}
      />
      <AddDashboardPanel
        isOpen={addDashboard !== undefined}
        dashboards={dashboards}
        currentDashboard={addDashboard}
        onSave={onCreateNewDashboard}
        onClose={() => {
          setAddDashboard(undefined);
        }}
      />
      <EditDashboardPanel
        isOpen={editDashboard !== undefined}
        dashboards={dashboards}
        currentDashboard={editDashboard}
        onSave={onEditDashboard}
        onClose={() => {
          setEditDashboard(undefined);
        }}
      />
      <ShareDashboardModal
        currentDashboard={shareDashboard}
        isOpen={shareDashboard !== undefined}
        onSave={onEditDashboard}
        onClose={() => setShareDashboard(undefined)}
      />
      {showConfirmationModal && (
        <LinkConfirmationModal
          links={[]}
          selectedLinkId={showConfirmationModalLinkId}
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
        />
      )}
    </IPStack>
  );
};

export default DashboardCollection;
